import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { H2, HeroH1, P, SeoPageShell, ctaClass } from "./seo/SeoPageLayout";
import { PasteAnalyzer } from "./CinematicLanding";

const PATH = "/free-gaslighting-detector";

const FAQ = [
  {
    q: "Is this gaslighting detector really free?",
    a: "Yes. Your first 3 previews are free with no account. Full reports, with quoted evidence and a severity score, are available when you sign in.",
  },
  {
    q: "Can a tool really detect gaslighting?",
    a: "It can detect the language patterns gaslighting uses, like denying events or questioning your memory. It can't know the history between you and the other person, so treat the result as a name for what you're seeing, not a verdict.",
  },
  {
    q: "Is my message private?",
    a: "Free previews run entirely in your browser, and your message is never sent to our servers. If you sign in and run a full analysis, the report is saved to your account's private Reports library.",
  },
  {
    q: "What else can it detect besides gaslighting?",
    a: "GreyRock checks for 18 manipulation patterns, including DARVO, guilt-tripping, invalidation, withdrawal pressure, and blame reversal.",
  },
  {
    q: "Does it work on screenshots?",
    a: "Not yet. Paste the text of the message instead.",
  },
];

const RELATED = [
  { to: "/spot-the-pattern", label: "Practice spotting the pattern with the free daily gaslighting quiz →" },
  { to: "/analysis/darvo-in-text-messages", label: "DARVO in text messages →" },
  { to: "/analysis/invalidation-patterns-in-texts", label: "Invalidation patterns in texts →" },
  { to: "/manipulative-text-message-analysis", label: "Manipulative text message analysis →" },
  { to: "/chatgpt-vs-greyrock-ai", label: "ChatGPT vs GreyRock AI →" },
];

const Li = ({ children }: { children: React.ReactNode }) => (
  <li className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">{children}</li>
);

const FreeGaslightingDetector = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "GreyRock AI",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const scrollToAnalyzer = () => {
    const el = document.getElementById("analyzer");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.querySelector("textarea")?.focus({ preventScroll: true });
  };

  return (
    <SeoPageShell>
      <SEO
        title="Free Gaslighting Detector: Paste a Text, See the Pattern | GreyRock AI"
        description="Free gaslighting detector for texts and DMs. Paste a message and see if it shows gaslighting or another manipulation pattern. No signup, and your message never leaves your browser."
        path={PATH}
        jsonLd={jsonLd}
      />

      <HeroH1>Free gaslighting detector</HeroH1>
      <p className="text-[18px] md:text-[19px] leading-[1.7] text-white/80">
        Paste a text, DM, or conversation and see whether it shows gaslighting or another
        manipulation pattern, named in plain language. No signup. Your message is analyzed in your
        browser and never sent to a server.
      </p>

      <div id="analyzer" className="mt-8 scroll-mt-24">
        <PasteAnalyzer size="md" />
      </div>

      <H2>How it works</H2>
      <ol className="list-decimal pl-6 space-y-2 mt-4">
        <Li><strong className="text-white/90">Paste the message.</strong> One line or a whole thread.</Li>
        <Li>
          <strong className="text-white/90">See the pattern.</strong> The free preview names the most
          serious manipulation pattern it finds, such as denying something happened or reframing
          your reaction as the problem.
        </Li>
        <Li>
          <strong className="text-white/90">Practice your reply.</strong> Three free turns
          against the pattern, then a short note on what to notice.
        </Li>
      </ol>

      <H2>What gaslighting sounds like in a text</H2>
      <P>
        Gaslighting is when someone makes you doubt your own memory or perception. In messages, it
        often shows up as:
      </P>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <Li>Denying events: "I never said that." "That never happened."</Li>
        <Li>Questioning your memory: "You're remembering it wrong." "You're imagining things."</Li>
        <Li>Recasting your reaction as the problem: "You're way too sensitive."</Li>
        <Li>Reversing the story: "You twist everything I say."</Li>
      </ul>
      <P>
        One of these on its own doesn't prove gaslighting. What matters is a pattern: the same
        moves, repeated, especially about things you know happened.
      </P>

      <H2>Gaslighting or an ordinary disagreement?</H2>
      <P>
        People remember things differently, and saying "that's not what I meant" isn't gaslighting.
        The difference is direction. In a normal disagreement, both people's memories count. In
        gaslighting, your version is consistently treated as unreliable, and the conversation moves
        from what happened to whether you can be trusted. GreyRock names the language pattern;
        you're the one who knows the history.
      </P>

      <H2>Free preview vs. full report</H2>
      <P>
        The free preview names the most serious pattern in a message. A full report checks the
        message against an 18-pattern taxonomy, quotes the exact words behind each pattern, scores
        severity on a fixed scale, and suggests calm "grey rock" replies. Full reports are saved to
        your private Reports library.
      </P>

      <H2>Your privacy</H2>
      <P>
        Free previews run entirely in your browser. Your message is never sent to our servers or
        stored. We record anonymous usage counts only, never your words.
      </P>

      <H2>What this tool isn't</H2>
      <P>
        GreyRock is not a diagnosis, a judgment about a person, or proof of intent, and it doesn't
        replace a therapist, lawyer, or crisis support. If you're in danger, contact local emergency
        services or a domestic violence hotline.
      </P>

      <section className="mt-16 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] px-6 py-10 text-center">
        <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-4">
          See what the message is actually doing
        </h2>
        <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 max-w-[520px] mx-auto">
          Paste a text and see which manipulation pattern it resembles. 3 free previews, no signup.
        </p>
        <div className="mt-8">
          <button type="button" onClick={scrollToAnalyzer} className={ctaClass}>
            Analyze a message →
          </button>
        </div>
      </section>

      <H2>FAQ</H2>
      {FAQ.map((f) => (
        <div key={f.q} className="mt-8 first:mt-0">
          <h3 className="text-[17px] md:text-[19px] font-semibold tracking-tight text-white mb-3 leading-snug">
            {f.q}
          </h3>
          <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">{f.a}</p>
        </div>
      ))}

      <div className="mt-14 pt-6 border-t border-white/[0.06]">
        <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-4 text-center">
          Related reading
        </p>
        <ul className="space-y-3 text-center">
          {RELATED.map((r) => (
            <li key={r.to}>
              <Link
                to={r.to}
                className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
              >
                {r.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </SeoPageShell>
  );
};

export default FreeGaslightingDetector;
